<script setup lang="ts">
import { ChevronDownIcon } from "@heroicons/vue/16/solid";
import { twMerge } from "tailwind-merge";
import { ref } from "vue";

import { DropdownMenu } from "@/components/ui/DropdownMenu";
import { useUser } from "@/composables/api/useUser";

import Button from "@/components/ui/Button.vue";

defineProps<{ hasActiveFilter: boolean }>();

const { data: user } = useUser();

const modelMonth = defineModel<string | null>("month", { required: true });
const modelYear = defineModel<string | null>("year", { required: true });

const months = [
  { label: "Janvier", value: "january" },
  { label: "Février", value: "february" },
  { label: "Mars", value: "march" },
  { label: "Avril", value: "april" },
  { label: "Mai", value: "may" },
  { label: "Juin", value: "june" },
  { label: "Juillet", value: "july" },
  { label: "Août", value: "august" },
  { label: "Septembre", value: "september" },
  { label: "Octobre", value: "october" },
  { label: "Novembre", value: "november" },
  { label: "Décembre", value: "december" },
];

const years = ["2025", "2026"];

const monthYearOptions = ref(
  years.flatMap((year) =>
    months.map((month) => ({
      label: `${month.label} ${year}`,
      month: month.value,
      year,
    })),
  ),
);

function resetFilter() {
  modelMonth.value = null;
  modelYear.value = null;
}

function getLabel() {
  if (modelMonth.value && modelYear.value) {
    const found = monthYearOptions.value.find(
      (o) => o.month === modelMonth.value && o.year === modelYear.value,
    );
    return found ? found.label : "Mois / Année";
  }
  return "Mois / Année";
}
</script>

<template>
  <div class="flex items-center gap-5 lg:gap-8" aria-label="Filtres de tickets">
    <DropdownMenu.Root>
      <template #trigger>
        <Button
          variant="secondary"
          class="gap-1 px-4 py-2 text-sm font-normal lg:gap-4 lg:px-5 lg:py-2.5 lg:text-base [&>svg]:size-4 lg:[&>svg]:size-5"
          aria-haspopup="true"
          aria-expanded="false"
        >
          {{ getLabel() }}
          <ChevronDownIcon aria-hidden="true" />
        </Button>
      </template>

      <template #content>
        <DropdownMenu.Item
          v-for="option in monthYearOptions"
          :key="option.label"
          :isActive="modelMonth === option.month && modelYear === option.year"
          @click="
            () => {
              modelMonth = option.month;
              modelYear = option.year;
            }
          "
        >
          {{ option.label }}
        </DropdownMenu.Item>
      </template>
    </DropdownMenu.Root>

    <button
      v-if="hasActiveFilter"
      type="button"
      :class="
        twMerge(
          'text-galbani cursor-pointer text-sm underline lg:text-base',
          user?.current_mechanic === 'lactel' && 'text-lactel-blue',
        )
      "
      aria-label="Réinitialiser tous les filtres"
      @click="resetFilter"
    >
      {{ $t("receipts.filter.reset") }}
    </button>
  </div>
</template>
